export interface EnvVars {
    CONVERSATION_UI_DEV?: string;
    SEGMENT_KEY?: string;
    PROMPT_TOKEN?: string;
    WEBAPP_HOST: string;
    TWILIO_SMS_OUTBOUND: string;
    SSO_HOST: string;
    COMMCONTROLLER_HOST: string;
    SECURITY_HOST: string;
    MESSENGER_HOST: string;
    DEV_TOOLS?: boolean;
    STORAGE_KEY: string;
    BUILD: string;
    CONVERSATION_LIST_HOST: string;
    MESSAGE_LIST_HOST: string;
    TRANSLATION_HOST: string;
    MESSAGE_TEMPLATE_HOST: string;
    SMARTLIST_HOST: string;
    SMARTLIST_FILTER_HOST: string;
    PROFANITY_SERVICE: string;
}

export interface UserSession {
    carrier_id: string;
    dealer_id: string;
    email: string;
    features: number[];
    firstname: string;
    id: string;
    lastLogin: string;
    lastname: string;
    permissions: number[];
    phone: string;
    role: string;
    sid?: string;
    sales_rep_id?: string;
    token?: string;
    user_active: string;
    user_language: UserLanguages;
    template_languages: string[];
    user_timezone: string;
    username: string;
}

export enum SocketMessages {
    UPDATE = 'commctrl.message.update',
    ERROR = 'commctrl.message.delivery_failed'
}

export enum MessageDeliveryStatuses {
    NEW = 'new',
    IN_PROGRESS = 'in-progress',
    SUCCESS = 'ok',
    ERROR = 'error',
    FAILED = 'failed',
    RETRY = 'retry',
    PROFANITY = 'profanity',
    DNC = 'dnc'
}

export type MessageDeliveryStatusType = MessageDeliveryStatuses;

export interface Message {
    action: MessageActionType;
    associated_number?: string;
    content: string;
    conversation_id?: number;
    currentSession?: boolean;
    date_added: string;
    delivery_status: MessageDeliveryStatusType;
    template?: boolean;
    id?: number | string;
    user_id?: number;
    new?: boolean;
    message_template_id?: string;
    templateId?: string;
    temporary_id?: string; // used to match sent message payloads with optimistic messages
    profaneWords?: object;
}

export type Messages = Message[];

export interface MessagesFeed {
    messages: Messages;
    total_count: number;
}

export interface MessagesRange {
    before?: number | string,
    after?: number | string
}


export interface Recipient {
    name: string,
    unit_number: string,
    ban_id: string,
    contact_id: number,
    phone_number?: string,
    activity_type: string,
    language: string,
}

export interface WaitingForReply {
    disabled: boolean,
    placeholder: string,
    warningMessage: string,
}

export interface FormObject {
    errorMessage?: object,
    required?: object,
    showErrors?: boolean,
    validated?: object,
    validations?: object,
    value?: any,
}

export interface CrmMessagesRow {
    phoneBanId: string,
    phoneTo: string,
    phoneCallType: number,
    phoneActionTaken: string,
    phoneOutcome: string,
    phoneMessage: string,
    phoneNextStep: string,
    phoneMeetingDate: string,
    phoneMeetingLocation: number,
    localTime: string,
}

export interface ErrorClass extends Error {
    response: string,
}

export interface Outlet {
    name: string;
    address_1: string;
    phone: string;
    dealer_id: string;
    code: string;
    postal_code: string;
    id: number;
    district_id: number;
    city: string;
    manager_phone: string;
    province: string;
    manager_email: string;
    web_address: string;
    manager_name: string;
    address_2: string;
    fax: string;
    country: string;
    locator?: string;
}

export interface Conversation {
    id: number,
    message: string,
    onSelectConversation?: () => void,
    recipientName: string,
    recipientNumber: number,
    last_message: Message,
    recipient?: Recipient
    unread_message_count?: number
}

export interface AlertType {
    message: string | null,
    type: string | null,
}

export interface Account {
    name: string,
    ban: string,
    banType: string,
    phone: string
}

export interface ContactOpportunity {
    name: string,
    date_added: string,
    expected_contract_expiry: string
}
export interface ContactSubscription {
    device: string,
    plan: string,
    start_date: string,
    expiration_date: string,
    balance: string
}

interface CpmData {
    SMS: boolean;
    CALL: boolean;
}

export interface Contact {
    id: number,
    name: string,
    phone: number,
    isPrimary: boolean,
    isDnc: boolean,
    cpmData: CpmData | undefined,
    account: Account,
    outlet: Outlet,
    subscription: ContactSubscription,
    opportunity: ContactOpportunity
}

export interface ConversationUrlParams {
    conversationId?: string;
}

export interface ConversationListItem {
    last_message: {
        associated_number_type: string;
        user_id: string;
        used_ip_address?: string;
        action: MessageActionType;
        content: string;
        associated_number: string;
        conversation_id: string;
        delivery_status: string;
        date_added: string;
        id: string;
    },
    recipient: {
        phone_number: string;
        ban_id: string;
        name: string;
    },
    unread_message_count: string;
    id: string;
}

export type ConversationList = ConversationListItem[];

export interface CommcontrollerPayload<TDetails> {
    details: TDetails;
    success: boolean;
}

export interface MessagesInputState {
    value: string,
}

export interface NextConversationPayload {
    activity_type?: string;
    ban_id: string;
    ban_no_url: string;
    ban_type: string;
    city: string;
    contact_id: string;
    client_name: string;
    contract_expiration?: string;
    conversation_full_url: string;
    conversation_id: number;
    conversation_url: string;
    exclude_dnc?: boolean;
    last_activity?: string;
    list_id?: number;
    name: string;
    outlet_name: string;
    phone_number: string;
}

export type sendMessage = (conversationId: number, messageText: string, activeLanguage: string, recipient?: Recipient, templateId?: string) => void;

export interface CustomFieldsType {
    editedValue: object;
    isDynamic: object;
    isValid: object;
    key: object;
    originalValue: object;
    order?: string[];
    templateId;
}

export interface CheckedDynamicField {
    fieldValue: string;
    hasProfanity: boolean;
}

export interface DynamicFieldInfo {
    customerName: string;
    salesRepName: string;
    dealership: string;
}

export type FieldName = string | null;

export enum ConversationFilterRoles {
    OUTLET_MANAGER = 3
    // DISTRICT_MANAGER = 7,
    // REGIONAL_MANAGER = 10,
    // DEALER_ADMIN     = 1
}

export enum FeatureFlag {
    CRM = 3,
    CUSTOM_BRANDING = 39,
    DISABLE_CONVERSATION_TYPE = 173,
    RING_CENTRAL_CALLING = 109,
    TWILIO_CALLING = 106,
    SMARTLIST = 193,
}

export enum Permissions {
    RING_CENTRAL_CALLING = 58
}

export type TranslationFunction = (key: string) => string;

export enum UserLanguages {
    ENGLISH = 'en',
    FRENCH = 'fr'
}

export type UserLanguagesType = UserLanguages.ENGLISH | UserLanguages.FRENCH;

export interface AppMessengerSocketError {
    message: Message;
    reason: string;
    result: string;
}

export enum MessageActions {
    SEND = 'send',
    RECEIVE = 'receive',
    AUTO = 'auto'
}

export type MessageActionType = MessageActions;



// WEBPACK FOOTER //
// ./src/types.ts